/**
 * Unified Storage Type Catalog
 *
 * A single master catalog of physical storage and transportation types used throughout the
 * application. Regulatory attributes (e-Manifest container codes, RCRA unit classifications) are
 * properties of each entry rather than separate taxonomies.
 *
 * Usage by reporting systems:
 *   - e-Manifest: use `defaultEManifestCode` (or let the user choose when multiple codes apply)
 *   - RCRA Biennial Report: use `rcraStationaryTank` to determine if the unit is a regulated tank
 *   - RCRA unit classification: derive from `category`
 */

import type { StorageTypeID } from '../cross-app-shared/types/storage-types.ts';

// ---------------------------------------------------------------------------
// e-Manifest container type codes (40 CFR Part 262 Appendix – EPA RCRAInfo)
// ---------------------------------------------------------------------------

/** EPA e-Manifest container type code as used in the RCRAInfo/e-Manifest API. */
export type EManifestContainerCode =
  | 'BA' // Burlap, cloth, paper, or plastic bags
  | 'CF' // Fiber or plastic boxes, cartons, cases
  | 'CM' // Metal boxes, cartons, cases (including roll-offs)
  | 'CW' // Wooden boxes, cartons, cases
  | 'CY' // Cylinders
  | 'DF' // Fiberboard or plastic drums, barrels, kegs
  | 'DM' // Metal drums, barrels, kegs
  | 'DT' // Dump truck
  | 'DW' // Wooden drums, barrels, kegs
  | 'HG' // Hopper or gondola cars
  | 'TC' // Tank cars
  | 'TP' // Portable tanks
  | 'TT'; // Cargo tanks (tank trucks)

/** Human-readable descriptions for e-Manifest container type codes. */
export const E_MANIFEST_CONTAINER_CODE_LABELS: Record<EManifestContainerCode, string> = {
  BA: 'Burlap, cloth, paper, or plastic bags',
  CF: 'Fiber or plastic boxes, cartons, cases',
  CM: 'Metal boxes, cartons, cases (including roll-offs)',
  CW: 'Wooden boxes, cartons, cases',
  CY: 'Cylinders',
  DF: 'Fiberboard or plastic drums, barrels, kegs',
  DM: 'Metal drums, barrels, kegs',
  DT: 'Dump truck',
  DW: 'Wooden drums, barrels, kegs',
  HG: 'Hopper or gondola cars',
  TC: 'Tank cars',
  TP: 'Portable tanks',
  TT: 'Cargo tanks (tank trucks)',
};

// ---------------------------------------------------------------------------
// Storage type catalog
// ---------------------------------------------------------------------------

/** High-level grouping for a storage type. */
export type StorageCategory =
  | 'Container'
  | 'Portable Tank / Container'
  | 'Tank'
  | 'Bulk Container'
  | 'Vehicle'
  | 'Rail'
  | 'Process Equipment'
  | 'Other';

/** A physical storage or transportation type entry in the master catalog. */
export interface StorageType {
  id: StorageTypeID;
  /** Display name shown to users. */
  name: string;
  category: StorageCategory;
  /**
   * Default e-Manifest container type code(s).
   *
   * - A single code when the mapping is unambiguous.
   * - An array when the physical type can be legitimately mapped to more than one code (e.g.
   *   "DM or DF" for a pail) — the user or the data entry workflow should select the most
   *   appropriate code at the time of manifest creation.
   * - `null` when no safe default applies, including stationary equipment and custom shipping packaging.
   */
  defaultEManifestCode: EManifestContainerCode | EManifestContainerCode[] | null;
  /**
   * True when this type is classified as a RCRA-regulated stationary tank unit (as opposed to a
   * container or portable tank).  Stationary tanks require tank-specific RCRA inspection and
   * recordkeeping under 40 CFR Part 265 Subpart J / Part 264 Subpart J.
   */
  rcraStationaryTank: boolean; // TODO: 3. Tier II/Hazwaste - This should likely be derived from rcraStationaryTankStorageTypeIDs or vice-versa.
  /**
   * Filename (relative to `hazwaste-shared/assets/waste_storage_containers/`) of the generated
   * artwork for this type, or `null` when no dedicated image has been produced yet.
   */
  imageFile: string | null;
}

/** The master storage type catalog. */
export const STORAGE_TYPES = [
  {
    id: 'biohazard-bag-red',
    name: 'Biohazard Bag (Red)',
    category: 'Container',
    defaultEManifestCode: 'BA',
    rcraStationaryTank: false,
    imageFile: 'biohazard_bag_red.png',
  },
  {
    id: 'biohazard-bag-yellow',
    name: 'Biohazard Bag (Yellow)',
    category: 'Container',
    defaultEManifestCode: 'BA',
    rcraStationaryTank: false,
    imageFile: 'biohazard_bag_yellow.png',
  },
  {
    id: 'universal-waste-lamp-container',
    name: 'Universal Waste Lamp Container',
    category: 'Container',
    defaultEManifestCode: 'CF',
    rcraStationaryTank: false,
    imageFile: 'universal_waste_lamp_container.png',
  },
  {
    id: 'sharps-container',
    name: 'Sharps Container',
    category: 'Container',
    defaultEManifestCode: ['CF', 'DF'],
    rcraStationaryTank: false,
    imageFile: 'sharps_container.png',
  },
  {
    id: 'glass-carboy',
    name: 'Glass Carboy',
    category: 'Container',
    defaultEManifestCode: null,
    rcraStationaryTank: false,
    imageFile: 'glass_carboy.png',
  },
  {
    id: 'custom-container',
    name: 'Custom Container',
    category: 'Container',
    defaultEManifestCode: null,
    rcraStationaryTank: false,
    imageFile: 'custom_container.png',
  },
  // -------------------------------------------------------------------------
  // Bags
  // -------------------------------------------------------------------------
  {
    id: 'bag',
    name: 'Bag',
    category: 'Container',
    defaultEManifestCode: 'BA',
    rcraStationaryTank: false,
    imageFile: null,
  },
  {
    id: 'super-sack-bulk-bag',
    name: 'Super Sack / Bulk Bag',
    category: 'Container',
    defaultEManifestCode: 'BA',
    rcraStationaryTank: false,
    imageFile: 'super_sack_bulk_bag.png',
  },

  // -------------------------------------------------------------------------
  // Boxes
  // -------------------------------------------------------------------------
  {
    id: 'fiber-plastic-box',
    name: 'Fiber / Plastic Box',
    category: 'Container',
    defaultEManifestCode: 'CF',
    rcraStationaryTank: false,
    imageFile: null,
  },
  {
    id: 'wooden-box',
    name: 'Wooden Box',
    category: 'Container',
    defaultEManifestCode: 'CW',
    rcraStationaryTank: false,
    imageFile: null,
  },
  {
    id: 'metal-box',
    name: 'Metal Box',
    category: 'Container',
    defaultEManifestCode: 'CM',
    rcraStationaryTank: false,
    imageFile: null,
  },
  {
    id: 'gaylord-box',
    name: 'Gaylord Box',
    category: 'Container',
    defaultEManifestCode: 'CF',
    rcraStationaryTank: false,
    imageFile: 'gaylord_box.png',
  },
  {
    id: 'battery-box-pallet',
    name: 'Battery Box / Battery Pallet',
    category: 'Container',
    defaultEManifestCode: ['CF', 'CM'] as EManifestContainerCode[],
    rcraStationaryTank: false,
    imageFile: 'battery_box_battery_pallet.png',
  },

  // -------------------------------------------------------------------------
  // Drums
  // -------------------------------------------------------------------------
  {
    id: 'metal-drum',
    name: 'Metal Drum',
    category: 'Container',
    defaultEManifestCode: 'DM',
    rcraStationaryTank: false,
    imageFile: null,
  },
  {
    id: 'fiber-plastic-drum',
    name: 'Fiber / Plastic Drum',
    category: 'Container',
    defaultEManifestCode: 'DF',
    rcraStationaryTank: false,
    imageFile: null,
  },
  {
    id: 'wooden-drum',
    name: 'Wooden Drum',
    category: 'Container',
    defaultEManifestCode: 'DW',
    rcraStationaryTank: false,
    imageFile: null,
  },
  {
    id: 'overpack-drum',
    name: 'Overpack Drum',
    category: 'Container',
    defaultEManifestCode: ['DM', 'DF'] as EManifestContainerCode[],
    rcraStationaryTank: false,
    imageFile: 'overpack_salvage_drum.png',
  },
  {
    id: 'lab-pack-drum',
    name: 'Lab Pack Drum',
    category: 'Container',
    defaultEManifestCode: ['DM', 'DF'] as EManifestContainerCode[],
    rcraStationaryTank: false,
    imageFile: 'lab_pack_drum.png',
  },
  {
    id: 'pail',
    name: 'Pail',
    category: 'Container',
    defaultEManifestCode: ['DM', 'DF'] as EManifestContainerCode[],
    rcraStationaryTank: false,
    imageFile: 'five_gallon_pail.png',
  },

  // -------------------------------------------------------------------------
  // Cylinders
  // -------------------------------------------------------------------------
  {
    id: 'cylinder',
    name: 'Cylinder',
    category: 'Container',
    defaultEManifestCode: 'CY',
    rcraStationaryTank: false,
    imageFile: null,
  },

  // -------------------------------------------------------------------------
  // Portable Tanks / IBC
  // -------------------------------------------------------------------------
  {
    id: 'ibc-tote',
    name: 'IBC Tote',
    category: 'Portable Tank / Container',
    defaultEManifestCode: 'TP',
    rcraStationaryTank: false,
    imageFile: 'ibc_tote.png',
  },
  {
    id: 'portable-tank',
    name: 'Portable Tank',
    category: 'Portable Tank / Container',
    defaultEManifestCode: 'TP',
    rcraStationaryTank: false,
    imageFile: 'polyethylene_tank.png',
  },

  // -------------------------------------------------------------------------
  // Stationary Tanks
  // -------------------------------------------------------------------------
  {
    id: 'vertical-aboveground-tank',
    name: 'Vertical Aboveground Tank',
    category: 'Tank',
    defaultEManifestCode: null,
    rcraStationaryTank: true,
    imageFile: 'vertical_aboveground_tank.png',
  },
  {
    id: 'horizontal-aboveground-tank',
    name: 'Horizontal Aboveground Tank',
    category: 'Tank',
    defaultEManifestCode: null,
    rcraStationaryTank: true,
    imageFile: 'horizontal_aboveground_tank.png',
  },
  {
    id: 'double-wall-tank',
    name: 'Double Wall Tank',
    category: 'Tank',
    defaultEManifestCode: null,
    rcraStationaryTank: true,
    imageFile: 'double_wall_tank.png',
  },

  // -------------------------------------------------------------------------
  // Portable / Temporary Tanks
  // -------------------------------------------------------------------------
  {
    id: 'mobile-frac-tank',
    name: 'Mobile Frac Tank',
    category: 'Portable Tank / Container',
    defaultEManifestCode: 'TP',
    rcraStationaryTank: false,
    imageFile: 'mobile_frac_tank.png',
  },

  // -------------------------------------------------------------------------
  // Roll-Offs and Dumpsters
  // -------------------------------------------------------------------------
  {
    id: 'roll-off-container',
    name: 'Roll Off Container',
    category: 'Container',
    defaultEManifestCode: 'CM',
    rcraStationaryTank: false,
    imageFile: 'roll_off_container.png',
  },
  {
    id: 'front-load-dumpster',
    name: 'Front Load Dumpster',
    category: 'Container',
    defaultEManifestCode: 'CM',
    rcraStationaryTank: false,
    imageFile: 'front_loading_dumpster.png',
  },
  {
    id: 'rear-load-dumpster',
    name: 'Rear Load Dumpster',
    category: 'Container',
    defaultEManifestCode: 'CM',
    rcraStationaryTank: false,
    imageFile: null,
  },
  {
    id: 'compactor-container',
    name: 'Compactor Container',
    category: 'Container',
    defaultEManifestCode: 'CM',
    rcraStationaryTank: false,
    imageFile: 'compactor_container.png',
  },

  // -------------------------------------------------------------------------
  // Specialty Containers
  // -------------------------------------------------------------------------
  {
    id: 'vacuum-box-sludge-box',
    name: 'Vacuum Box / Sludge Box',
    category: 'Container',
    defaultEManifestCode: ['CM', 'TP'] as EManifestContainerCode[],
    rcraStationaryTank: false,
    imageFile: 'vacuum_box_sludge_box.png',
  },

  // -------------------------------------------------------------------------
  // Bulk Containers
  // -------------------------------------------------------------------------
  {
    id: 'hopper',
    name: 'Hopper',
    category: 'Bulk Container',
    defaultEManifestCode: ['HG', 'CM'] as EManifestContainerCode[],
    rcraStationaryTank: false,
    imageFile: 'hopper_bulk_solids_bin.png',
  },

  // -------------------------------------------------------------------------
  // Vehicles
  // -------------------------------------------------------------------------
  {
    id: 'dump-truck',
    name: 'Dump Truck',
    category: 'Vehicle',
    defaultEManifestCode: 'DT',
    rcraStationaryTank: false,
    imageFile: null,
  },
  {
    id: 'cargo-tank-truck',
    name: 'Cargo Tank Truck',
    category: 'Vehicle',
    defaultEManifestCode: 'TT',
    rcraStationaryTank: false,
    imageFile: null,
  },

  // -------------------------------------------------------------------------
  // Rail
  // -------------------------------------------------------------------------
  {
    id: 'tank-car',
    name: 'Tank Car',
    category: 'Rail',
    defaultEManifestCode: 'TC',
    rcraStationaryTank: false,
    imageFile: null,
  },
  {
    id: 'hopper-gondola-rail-car',
    name: 'Hopper / Gondola Rail Car',
    category: 'Rail',
    defaultEManifestCode: 'HG',
    rcraStationaryTank: false,
    imageFile: null,
  },

  // -------------------------------------------------------------------------
  // Process Equipment
  // -------------------------------------------------------------------------
  {
    id: 'small-parts-washer',
    name: 'Small Parts Washer',
    category: 'Process Equipment',
    defaultEManifestCode: null,
    rcraStationaryTank: false,
    imageFile: 'small_parts_washing_container.png',
  },
  {
    id: 'other',
    name: 'Other',
    category: 'Other',
    defaultEManifestCode: null,
    rcraStationaryTank: false,
    imageFile: null,
  },
] as const satisfies StorageType[];

/** Lookup map from StorageTypeID to StorageType entry. */
export const STORAGE_TYPE_BY_ID: Record<StorageTypeID, StorageType> = Object.fromEntries(
  STORAGE_TYPES.map((t) => [t.id, t]),
) as Record<StorageTypeID, StorageType>;

// ---------------------------------------------------------------------------
// Related Infrastructure Assets
// ---------------------------------------------------------------------------
// These are not storage containers but are tracked in the application because
// dedicated artwork has been generated for them.

/** Stable identifier for an accumulation area type. */
export type AccumulationAreaTypeID = 'satellite-accumulation-area' | 'central-accumulation-area';

export interface AccumulationAreaType {
  id: AccumulationAreaTypeID;
  name: string;
}

export const ACCUMULATION_AREA_TYPES: AccumulationAreaType[] = [
  { id: 'satellite-accumulation-area', name: 'Satellite Accumulation Area' },
  { id: 'central-accumulation-area', name: 'Central Accumulation Area' },
];

/** Stable identifier for a secondary containment type.
 *
 * IDs for the first nine types match the `FacilityStorageSecondaryContainmentType` DB constraint
 * values exactly — these are DB-persisted and require a migration before adding new ones to the
 * constraint.  The remaining IDs (concrete-berm onward) are catalog-only entries that cannot yet
 * be persisted; they are listed here so the UI can be extended once the DB is migrated. */
export type SecondaryContainmentTypeID =
  // ── DB-valid (FacilityStorageSecondaryContainmentType) ──────────────────
  | 'spill-containment-pallet'
  | 'poly-containment-tub'
  | 'portable-spill-berm'
  | 'curbed-concrete-pad'
  | 'grated-containment-pad'
  | 'concrete-containment-basin'
  | 'covered-containment-sump'
  | 'earthen-berm'
  | 'conex-box'
  // ── Catalog-only (not yet in DB constraint) ─────────────────────────────
  | 'concrete-berm'
  | 'containment-trench'
  | 'double-wall-containment'
  | 'lined-containment-area'
  | 'indoor-containment-room'
  | 'loading-dock-containment'
  | 'sump'
  | 'open-containment-area'
  | 'other-secondary-containment';

export interface SecondaryContainmentType {
  id: SecondaryContainmentTypeID;
  name: string;
  /**
   * Filename (relative to `hazwaste-shared/assets/secondary-containment-types/`) of the
   * generated artwork for this type, or `null` when no dedicated image has been produced yet.
   */
  imageFile: string | null;
}

export const SECONDARY_CONTAINMENT_TYPES: SecondaryContainmentType[] = [
  // ── DB-valid entries (match FacilityStorageSecondaryContainmentType exactly) ──
  { id: 'spill-containment-pallet', name: 'Spill Containment Pallet', imageFile: 'spill-containment-pallet.png' },
  { id: 'poly-containment-tub', name: 'Poly Containment Tub / Spill Deck', imageFile: 'poly-containment-tub.png' },
  { id: 'portable-spill-berm', name: 'Portable Spill Berm', imageFile: 'portable-spill-berm.png' },
  { id: 'curbed-concrete-pad', name: 'Curbed Concrete Pad', imageFile: 'curbed-concrete-pad.png' },
  { id: 'grated-containment-pad', name: 'Grated / Roofed Containment Pad', imageFile: 'grated-containment-pad.png' },
  { id: 'concrete-containment-basin', name: 'Concrete Containment Basin', imageFile: 'concrete-containment-basin.png' },
  { id: 'covered-containment-sump', name: 'Covered Containment Sump', imageFile: 'covered-containment-sump.png' },
  { id: 'earthen-berm', name: 'Earthen Berm', imageFile: 'earthen-berm.png' },
  { id: 'conex-box', name: 'Conex Box / Storage Container', imageFile: 'conex-box.png' },
  // ── Catalog-only (not yet DB-persisted; require a migration to enable) ──
  { id: 'concrete-berm', name: 'Concrete Berm', imageFile: null },
  { id: 'containment-trench', name: 'Containment Trench', imageFile: null },
  { id: 'double-wall-containment', name: 'Double Wall Containment', imageFile: null },
  { id: 'lined-containment-area', name: 'Lined Containment Area', imageFile: null },
  { id: 'indoor-containment-room', name: 'Indoor Containment Room', imageFile: null },
  { id: 'loading-dock-containment', name: 'Loading Dock Containment', imageFile: null },
  { id: 'sump', name: 'Sump', imageFile: null },
  { id: 'open-containment-area', name: 'Open Containment Area', imageFile: null },
  { id: 'other-secondary-containment', name: 'Other Secondary Containment', imageFile: null },
];
